import { Link } from "react-router-dom";

/**
 * Politique de confidentialité — page publique linkée dans :
 *  - le footer de chaque email envoyé via Smartlead
 *  - le bas de la page de login
 *
 * Contenu obligatoire RGPD (cf. docs/ARCHITECTURE.md §9).
 *
 * Infos juridiques renseignées (SARL, RCS/SIREN Rennes 944 904 465, SIRET 944 904 465 00019, siège Rennes).
 * À publier sur une URL publique avant le 1er envoi (cf. ticket dédié hébergement).
 */
export function Privacy() {
  return (
    <div className="min-h-screen bg-background">
      {/* Bandeau hero (cohérence avec le site live) */}
      <header className="aurora-hero-gradient px-6 py-12 text-white">
        <div className="mx-auto flex max-w-3xl items-center gap-4">
          <div className="flex items-center justify-center rounded-lg bg-white px-3 py-1.5">
            <img src="/logo-aurora.png" alt="Aurora Digital Solutions" className="h-8 w-auto" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-white/70">
              Aurora Digital Solutions
            </div>
            <h1 className="text-2xl font-bold">Politique de confidentialité</h1>
          </div>
        </div>
      </header>

      <article className="mx-auto max-w-3xl px-6 py-12">
        <p className="mb-10 text-sm text-neutral-500">Dernière mise à jour : 12 juin 2026</p>

        <div className="space-y-8 text-base leading-relaxed text-neutral-700">
          <Section title="1. Identité du responsable de traitement">
            <p>
              <strong>Aurora Digital Solutions</strong>, SARL au capital de 5 000 €, immatriculée au
              RCS de Rennes sous le numéro 944 904 465 (SIREN), SIRET 944 904 465 00019, dont le
              siège social est situé 22 rue Michelet, 35700 Rennes (France), représentée par Alain
              Tritschler.
            </p>
            <p>
              Contact pour toute question relative à la protection des données :{" "}
              <a
                href="mailto:contact@aurora-ds.com"
                className="text-aurora-bright-blue hover:underline"
              >
                contact@aurora-ds.com
              </a>
              .
            </p>
          </Section>

          <Section title="2. Finalité du traitement">
            <p>
              Aurora Digital Solutions met en œuvre un traitement de données à caractère personnel à
              des fins de <strong>prospection commerciale B2B</strong> : identification de prospects
              professionnels susceptibles d'être intéressés par nos services en intelligence
              artificielle, prise de contact initiale par e-mail, gestion des suites données.
            </p>
          </Section>

          <Section title="3. Base légale">
            <p>
              Le traitement repose sur l'<strong>intérêt légitime</strong> d'Aurora Digital
              Solutions à promouvoir ses services auprès de professionnels (article 6.1.f du RGPD),
              dans le respect des recommandations de la CNIL sur la prospection commerciale B2B.
            </p>
          </Section>

          <Section title="4. Données collectées">
            <p>Sont susceptibles d'être collectées et traitées :</p>
            <ul className="list-inside list-disc space-y-1 pl-2">
              <li>Identité professionnelle : prénom, nom, fonction</li>
              <li>
                Coordonnées professionnelles : e-mail, numéro de téléphone professionnel, profil
                LinkedIn
              </li>
              <li>Informations sur l'employeur : raison sociale, secteur, taille, site web</li>
              <li>Historique des échanges (emails reçus, ouvertures, réponses, désabonnements)</li>
            </ul>
            <p>
              <strong>Aucune donnée sensible</strong> (santé, opinions politiques ou religieuses,
              orientation sexuelle, etc.) n'est collectée ni traitée.
            </p>
          </Section>

          <Section title="5. Source des données">
            <p>
              Les données sont issues de <strong>sources accessibles publiquement</strong> : profils
              professionnels LinkedIn, sites web d'entreprises, annuaires professionnels, bases de
              données B2B (Apollo, etc.) — uniquement dans le cadre d'usages compatibles avec leurs
              conditions générales.
            </p>
          </Section>

          <Section title="6. Durée de conservation">
            <p>
              Les données sont conservées pendant <strong>3 ans</strong> à compter du dernier
              contact resté sans réponse, conformément aux recommandations de la CNIL. À
              l'expiration de ce délai, elles sont supprimées de nos systèmes.
            </p>
            <p>
              Les demandes d'opposition (désabonnement) sont conservées au minimum 3 ans afin de
              garantir le respect du choix de la personne.
            </p>
          </Section>

          <Section title="7. Destinataires">
            <p>
              Les données sont accessibles uniquement par les équipes habilitées d'Aurora Digital
              Solutions. Elles peuvent être traitées par nos sous-traitants techniques (hébergement,
              envoi d'e-mails, vérification d'adresse), engagés contractuellement à respecter le
              RGPD :
            </p>
            <ul className="list-inside list-disc space-y-1 pl-2">
              <li>Supabase (hébergement de la base de données — UE)</li>
              <li>Smartlead (envoi des emails et gestion délivrabilité — US, conditions DPF)</li>
              <li>Anthropic (génération de l'accroche par Claude — US, conditions DPF)</li>
              <li>Google Workspace (hébergement des mailboxes — UE)</li>
              <li>Apify (sourcing — UE)</li>
              <li>MillionVerifier (vérification d'email — UE)</li>
            </ul>
          </Section>

          <Section title="8. Vos droits">
            <p>Conformément au RGPD, vous disposez des droits suivants sur vos données :</p>
            <ul className="list-inside list-disc space-y-1 pl-2">
              <li>Droit d'accès et de copie</li>
              <li>Droit de rectification</li>
              <li>Droit d'opposition au traitement</li>
              <li>Droit à l'effacement</li>
              <li>Droit à la portabilité</li>
              <li>Droit d'introduire une réclamation auprès de la CNIL</li>
            </ul>
            <p>
              Pour exercer ces droits, contactez-nous à{" "}
              <a
                href="mailto:contact@aurora-ds.com"
                className="text-aurora-bright-blue hover:underline"
              >
                contact@aurora-ds.com
              </a>
              . Nous traiterons votre demande sous <strong>1 mois</strong> maximum.
            </p>
            <p>
              <strong>Pour vous désabonner immédiatement</strong> : utilisez le lien "Ne plus
              recevoir d'email" présent en bas de chaque email reçu. Votre désabonnement est traité
              automatiquement et sous 24 h.
            </p>
          </Section>

          <Section title="9. Sécurité">
            <p>
              Les données sont stockées sur des serveurs sécurisés, avec chiffrement en transit
              (TLS) et au repos. L'accès est restreint par authentification forte et journalisé.
            </p>
          </Section>

          <Section title="10. Réclamation auprès de la CNIL">
            <p>
              Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une
              réclamation auprès de la Commission Nationale de l'Informatique et des Libertés (CNIL)
              :{" "}
              <a
                href="https://www.cnil.fr/fr/plaintes"
                target="_blank"
                rel="noreferrer noopener"
                className="text-aurora-bright-blue hover:underline"
              >
                cnil.fr/fr/plaintes
              </a>
              .
            </p>
          </Section>
        </div>

        <footer className="mt-16 border-t border-neutral-200 pt-6 text-sm">
          <Link to="/login" className="text-aurora-teal hover:text-aurora-orange">
            ← Retour à la connexion
          </Link>
        </footer>
      </article>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="mb-4 border-b-2 border-aurora-orange pb-2 text-xl font-bold text-aurora-navy">
        {title}
      </h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
